import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/naming-convention */ import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import { useBoolean } from 'ahooks';
import { Col, Row, Space } from 'antd';
import { isEmpty } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { RoleType, UserType } from 'api/models';
import { ReactComponent as Astronaut } from 'assets/svgs/astro.svg';
import { Checkbox } from 'components/atoms/checkbox';
import { Form } from 'components/atoms/form';
import { Loading as LoadingAtom } from 'components/atoms/loading';
import { Modal } from 'components/atoms/modal';
import { Tag } from 'components/atoms/tag';
import { TimedRender } from 'components/atoms/utils/timed-render';
import { Button } from 'components/facade/button';
import { Select } from 'components/facade/select';
import { BodyLong01, ProductiveHeading05, ProductiveHeading06 } from 'components/styles';
import { useTranslation, useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useUpdateCurrentUserMutation, useUpdateUserMetaMutation } from 'queries';
import { useAccountDataQuery } from 'queries/account';
import { useUpdateHubspotMutation } from 'queries/hubspot';
import { useCreateRoleMutation, useVendorRolesQuery } from 'queries/roles';
import { logger } from 'services';
import { byLabel } from 'utils/comparators/by-label';
import { IPINFO_URL, RH_EVENT_ORIGIN } from './constants';
export const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding: 0 !important;
  }
`;
export const modalMaskCSS = {
    zIndex: 1011
};
const Title = styled.div`
  ${ProductiveHeading05};
`;
const Description = styled.div`
  ${BodyLong01};
`;
const WelcomeNote = styled(Col)`
  background-color: var(--gray-10);
`;
const PaddedContainer = styled.div`
  padding: var(--spacing-08) var(--spacing-06);
`;
const SpaceBetween = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  height: 100%;
`;
export const LoadingSpinner = styled(LoadingAtom)`
  padding: var(--spacing-03);
`;
const SvgContainer = styled.div`
  display: flex;
`;
const FormContainer = styled.div`
  width: var(--pane-width);
  padding: var(--spacing-07) var(--spacing-07) 0 var(--spacing-07);
`;
const Heading = styled.h6`
  ${ProductiveHeading06};
  margin-bottom: var(--layout-02);
`;
const Footer = styled.div`
  padding: var(--spacing-05);
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: flex-end;
`;
const ModalHeightCSS = css`
  height: 380px;
`;
const VideoContainer = styled(Col)`
  ${ModalHeightCSS};
`;
const Iframe = styled.iframe`
  ${ModalHeightCSS};
  width: 100%;
`;
const CloseButtonContainer = styled.div`
  position: absolute;
  right: 0;
  top: -40px;
`;
const ModalCloseButton = styled(Tag)`
  background-color: var(--gray-80);
  color: var(--white-100);
  border: 2px solid transparent;
  cursor: pointer;
  ${({ disabled })=>disabled ? css`
          pointer-events: none;
        ` : ''};
`;
export const RHTitle = styled.div`
  padding: var(--spacing-05);
  padding-bottom: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  ${ProductiveHeading06};

  span {
    width: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;
export const RHContainer = styled.div`
  height: ${({ height })=>height}px;
  width: 672px;

  div {
    position: unset !important;
  }
`;
export const CloseButton = styled(Button)`
  position: absolute;
  top: var(--spacing-03);
  right: var(--spacing-03);
  z-index: var(--zindex-overlay-content);
`;
const GOAL_OPTIONS = [
    {
        key: 'routes.vendor.accelerate_timetovalue',
        value: 'Accelerate time-to-value'
    },
    {
        key: 'routes.vendor.enhance_customer_visibility',
        value: 'Enhance customer visibility'
    },
    {
        key: 'routes.vendor.seamless_timetracking',
        value: 'Seamless time-tracking'
    },
    {
        key: 'routes.vendor.improve_utilization_rates',
        value: 'Improve utilization rates'
    },
    {
        key: 'routes.vendor.create_custom_reports',
        value: 'Create custom reports'
    }
];
const INDUSTRY_OPTIONS = [
    {
        key: 'routes.vendor.saas_onboarding_and_implementation',
        value: 'SaaS - Onboarding and Implementation'
    },
    {
        key: 'routes.vendor.saas_professional_services',
        value: 'SaaS - Professional Services'
    },
    {
        key: 'routes.vendor.services_and_consultancy',
        value: 'Services and Consultancy'
    },
    {
        key: 'routes.vendor.agency',
        value: 'Agency'
    },
    {
        key: 'routes.vendor.others',
        value: 'Others'
    }
];
const TEAM_OPTIONS = [
    '1 to 10',
    '11 to 50',
    '51 to 200',
    '200+'
];
/**
 * Key - User facing labels + Hubspot value
 * Value - Role name in Rocketlane
 */ const ROLES_MAP = {
    'Executives (C-suite, VP, Director)': 'Executive',
    'Implementation & delivery': 'Implementation Manager',
    'Onboarding Management': 'Onboarding Manager',
    'Project Management': 'Project Manager',
    'Resource Management': 'Resource Manager',
    Engineering: 'Engineer',
    Others: 'Others'
};
const ROLES_TRANSLATION_KEYS_MAP = {
    'Executives (C-suite, VP, Director)': 'routes.vendor.roles.executive',
    'Implementation & delivery': 'routes.vendor.roles.implementation_manager',
    'Onboarding Management': 'routes.vendor.roles.onboarding_management',
    'Project Management': 'routes.vendor.roles.project_management',
    'Resource Management': 'routes.vendor.roles.resource_management',
    Engineering: 'routes.vendor.roles.engineer',
    Others: 'routes.vendor.roles.others'
};
export var IntroModalWidth = /*#__PURE__*/ function(IntroModalWidth) {
    IntroModalWidth[IntroModalWidth["RevenueHeroWidget"] = 672] = "RevenueHeroWidget";
    IntroModalWidth[IntroModalWidth["FormModal"] = 812] = "FormModal";
    return IntroModalWidth;
}({});
export var RevenueHeroState = /*#__PURE__*/ function(RevenueHeroState) {
    RevenueHeroState[RevenueHeroState["Loading"] = 0] = "Loading";
    RevenueHeroState[RevenueHeroState["Disqualified"] = 1] = "Disqualified";
    RevenueHeroState[RevenueHeroState["Qualified"] = 2] = "Qualified";
    return RevenueHeroState;
}({});
const RH_TARGET = 'revenuehero_target';
const convertFormValuesToHubspotPayload = (values)=>{
    return Object.entries(values).reduce((obj, entry)=>{
        const [key, value] = entry;
        if (Array.isArray(value)) {
            // For checkboxes, Hubspot expects a semicolon separated string
            obj[key] = Object.values(convertFormValuesToHubspotPayload(value)).join(';');
        } else if (typeof value === 'object') {
            obj[key] = value.label;
        } else {
            obj[key] = value;
        }
        return obj;
    }, {});
};
export const injectRHWidget = ({ emailId, rhState, setRhState, setRHData, onFinish })=>{
    if (!emailId) {
        return;
    }
    const _window = window;
    const script = document.createElement('script');
    script.onload = async ()=>{
        _window.rhWidget = new _window.RevenueHero({
            routerId: '1440',
            greetingText: ''
        });
        let country = '';
        try {
            const ipResponse = await fetch(IPINFO_URL);
            const ipData = await ipResponse.json();
            country = ipData.country;
        } catch (e) {
            logger.error('Unable to fetch IP address', {
                error: e
            });
        }
        _window.rhWidget.submit({
            email: emailId,
            ip_detected_country: country
        }).then((data)=>{
            if (data) {
                setRHData(data);
                setRhState(2);
            } else if (rhState === 0) {
                // User was disqualified from the meeting
                setRhState(1);
            } else {
                // User was disqualified and the widget is being shown currently, so close it
                onFinish();
            }
        });
    };
    script.src = 'https://app.revenuehero.io/scheduler.min.js';
    document.body.appendChild(script);
};
const RHWidget = ({ close, form, rhData })=>{
    const { rhWidget: hero } = window;
    const loading = !hero || !rhData;
    const [rhHeight, setRHHeight] = useState(489);
    const [isBooking, setIsBooking] = useState(true);
    useEffect(()=>{
        if (loading) {
            return;
        }
        const messageEventCallback = (event)=>{
            var _event_data, _event_data1;
            if (event.origin !== RH_EVENT_ORIGIN || !event.isTrusted) {
                return;
            }
            if (((_event_data = event.data) === null || _event_data === void 0 ? void 0 : _event_data.type) === 'RESIZE_IFRAME') {
                setRHHeight(event.data.height + 4);
            } else if (((_event_data1 = event.data) === null || _event_data1 === void 0 ? void 0 : _event_data1.type) === 'MEETING_BOOKED') {
                setIsBooking(false);
            }
        };
        try {
            window.addEventListener('message', messageEventCallback);
            hero.dialog.setEmbedTarget(`#${RH_TARGET}`);
            hero.dialog.open(rhData);
        } catch (e) {
            logger.error('Unable to show Revenue Hero widget', {
                error: e
            });
            close();
        }
        // eslint-disable-next-line consistent-return
        return ()=>{
            window.removeEventListener('message', messageEventCallback);
        };
    }, [
        form,
        close,
        rhData,
        loading,
        hero === null || hero === void 0 ? void 0 : hero.dialog
    ]);
    if (loading) {
        return /*#__PURE__*/ _jsx(_Fragment, {
            children: /*#__PURE__*/ _jsx(LoadingSpinner, {
                size: "large"
            })
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(RHTitle, {
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        children: isBooking && /*#__PURE__*/ _jsx(FormattedMessage, {
                            id: "signup_onboarding_rh"
                        })
                    }),
                    /*#__PURE__*/ _jsx(CloseButton, {
                        type: "text",
                        onClick: close,
                        children: /*#__PURE__*/ _jsx(FormattedMessage, {
                            id: isBooking ? 'skip' : 'close'
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(RHContainer, {
                id: RH_TARGET,
                height: rhHeight
            })
        ]
    });
};
const IntroVideo = ({ firstName, onClose })=>{
    const { userMeta } = useUser();
    const metaData = userMeta === null || userMeta === void 0 ? void 0 : userMeta.metaData;
    const { mutate, isLoading } = useUpdateUserMetaMutation();
    const handleClose = ()=>{
        mutate({
            metaData: {
                ...metaData,
                profileSetupDone: true
            }
        });
        onClose();
    };
    const translate = useTranslate();
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(CloseButtonContainer, {
                children: /*#__PURE__*/ _jsx(ModalCloseButton, {
                    onClick: handleClose,
                    disabled: isLoading,
                    children: /*#__PURE__*/ _jsx(FormattedMessage, {
                        id: "close"
                    })
                })
            }),
            /*#__PURE__*/ _jsxs(Row, {
                gutter: 0,
                children: [
                    /*#__PURE__*/ _jsx(WelcomeNote, {
                        span: 8,
                        children: /*#__PURE__*/ _jsx(PaddedContainer, {
                            children: /*#__PURE__*/ _jsxs(Space, {
                                direction: "vertical",
                                size: 32,
                                children: [
                                    /*#__PURE__*/ _jsx(Title, {
                                        children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                            id: "intro_modal.title",
                                            values: {
                                                firstName,
                                                b: (chunks)=>/*#__PURE__*/ _jsx("b", {
                                                        children: chunks
                                                    })
                                            }
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Description, {
                                        children: /*#__PURE__*/ _jsxs(Space, {
                                            direction: "vertical",
                                            size: 24,
                                            children: [
                                                /*#__PURE__*/ _jsx(FormattedMessage, {
                                                    id: "intro_modal.message.1"
                                                }),
                                                /*#__PURE__*/ _jsx(FormattedMessage, {
                                                    id: "intro_modal.message.2"
                                                })
                                            ]
                                        })
                                    })
                                ]
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(VideoContainer, {
                        span: 16,
                        children: /*#__PURE__*/ _jsx(Iframe, {
                            src: "https://www.youtube.com/embed/Md0b2WaAUj4?si=m80ieirq8TTSghSJ",
                            title: translate('routes.vendor.rocketlane_overview'),
                            frameBorder: "0",
                            allow: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
                            allowFullScreen: true
                        })
                    })
                ]
            })
        ]
    });
};
const IntroModalContent = ({ firstName, emailId, userType, originalRole, onFinish, rhState, setRhState })=>{
    var _accountData_billingMeta_accountAdmin, _accountData_billingMeta;
    const { data: roles, isLoading: isRolesLoading } = useVendorRolesQuery();
    const { mutateAsync: updateMeta, isLoading: isUpdatingMeta } = useUpdateUserMetaMutation();
    const { mutateAsync: updateHubspot, isLoading: isUpdatingHubspot } = useUpdateHubspotMutation();
    const { mutateAsync: updateCurrentUser, isLoading: isUpdatingUser } = useUpdateCurrentUserMutation();
    const { mutateAsync: createRole, isLoading: isCreatingRole } = useCreateRoleMutation();
    const { user: { userId } } = useUser();
    const { data: accountData } = useAccountDataQuery();
    const isAccountOwner = !!userId && (accountData === null || accountData === void 0 ? void 0 : (_accountData_billingMeta = accountData.billingMeta) === null || _accountData_billingMeta === void 0 ? void 0 : (_accountData_billingMeta_accountAdmin = _accountData_billingMeta.accountAdmin) === null || _accountData_billingMeta_accountAdmin === void 0 ? void 0 : _accountData_billingMeta_accountAdmin.userId) === userId;
    const [form] = Form.useForm();
    const roleValidationText = useTranslation('intro_modal.form.role.validation.required');
    const teamValidationText = useTranslation('intro_modal.form.team.validation.required');
    const industryValidationText = useTranslation('intro_modal.form.industry.validation.required');
    const goalsValidationText = useTranslation('intro_modal.form.goals.validation.required');
    const translate = useTranslate();
    const handleSubmit = async (values)=>{
        const { role } = values;
        let newlyCreatedRole;
        if (role.__isNew__) {
            newlyCreatedRole = await createRole({
                roleName: role.value,
                use_as_placeholder: false,
                type: RoleType.Vendor
            });
        }
        const completeProfileSetup = async ()=>{
            await updateMeta({
                metaData: {
                    profileSetupDone: true
                }
            }, {
                onSuccess: ()=>{
                    if (rhState === 1) {
                        // User did not qualify for a meeting
                        onFinish();
                    } else if (userType !== UserType.Partner) {
                        setRhState(0);
                    } else {
                        onFinish();
                    }
                }
            });
        };
        await updateCurrentUser({
            role: {
                roleId: newlyCreatedRole ? newlyCreatedRole.roleId : role.value,
                roleName: newlyCreatedRole ? newlyCreatedRole.roleName : ROLES_MAP[role.label],
                type: newlyCreatedRole ? newlyCreatedRole.type : RoleType.Vendor
            }
        }, {
            onSuccess: async ()=>{
                if (isAccountOwner) {
                    await updateHubspot({
                        user: convertFormValuesToHubspotPayload(values)
                    }, {
                        onSuccess: completeProfileSetup
                    });
                } else {
                    await completeProfileSetup();
                }
            }
        });
    };
    const rolesOptions = useMemo(()=>{
        const isPartner = userType === UserType.Partner;
        const availableRoles = isPartner ? (roles !== null && roles !== void 0 ? roles : []).filter(({ usedByPartners })=>usedByPartners === true) : roles !== null && roles !== void 0 ? roles : [];
        if (isPartner) {
            return availableRoles.map(({ roleName, roleId })=>({
                    label: roleName,
                    value: roleId
                })).sort(byLabel);
        }
        return Object.entries(ROLES_MAP).map(([label, roleName])=>{
            var _ref;
            const existingRole = availableRoles.find((role)=>role.roleName === roleName);
            return {
                label: translate(ROLES_TRANSLATION_KEYS_MAP[label]),
                value: (_ref = existingRole === null || existingRole === void 0 ? void 0 : existingRole.roleId) !== null && _ref !== void 0 ? _ref : roleName,
                __isNew__: existingRole === undefined
            };
        });
    }, [
        roles,
        userType,
        translate
    ]);
    const isLoading = isCreatingRole || isUpdatingHubspot || isUpdatingMeta || isUpdatingUser;
    const showAccountCreateOnboardingModal = isEmpty(originalRole) || isLoading;
    const [rhData, setRHData] = useState(null);
    useEffect(()=>{
        if (rhState === 0 && userType !== UserType.Partner && window.rhWidget === undefined) {
            // Run only once
            injectRHWidget({
                emailId,
                rhState,
                setRhState,
                setRHData,
                onFinish
            });
        }
    }, [
        emailId,
        rhState,
        setRhState,
        setRHData,
        onFinish,
        userType,
        originalRole
    ]);
    if (rhState === 2) {
        return /*#__PURE__*/ _jsx(TimedRender, {
            hideAfterMs: 2500,
            retainIf: ()=>rhData !== null,
            onHide: ()=>{
                logger.warn('Revenue Hero widget did not load in time');
                onFinish();
            },
            children: /*#__PURE__*/ _jsx(RHWidget, {
                form: form,
                rhData: rhData,
                close: onFinish
            })
        });
    }
    if (!showAccountCreateOnboardingModal) {
        return /*#__PURE__*/ _jsx(IntroVideo, {
            firstName: firstName,
            onClose: onFinish
        });
    }
    return /*#__PURE__*/ _jsxs(Row, {
        gutter: 0,
        children: [
            /*#__PURE__*/ _jsx(WelcomeNote, {
                flex: "246px",
                children: /*#__PURE__*/ _jsxs(SpaceBetween, {
                    children: [
                        /*#__PURE__*/ _jsx(PaddedContainer, {
                            children: /*#__PURE__*/ _jsxs(Space, {
                                direction: "vertical",
                                size: 32,
                                children: [
                                    /*#__PURE__*/ _jsx(Title, {
                                        children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                            id: "intro_modal.title",
                                            values: {
                                                firstName,
                                                b: (chunks)=>/*#__PURE__*/ _jsx("b", {
                                                        children: chunks
                                                    })
                                            }
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Description, {
                                        children: /*#__PURE__*/ _jsx(Space, {
                                            direction: "vertical",
                                            size: 24,
                                            children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                                id: "intro_modal.message.1"
                                            })
                                        })
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(SvgContainer, {
                            children: /*#__PURE__*/ _jsx(Astronaut, {})
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(Col, {
                flex: "auto",
                children: [
                    /*#__PURE__*/ _jsxs(FormContainer, {
                        children: [
                            /*#__PURE__*/ _jsx(Heading, {
                                children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                    id: "intro_modal.form.heading"
                                })
                            }),
                            /*#__PURE__*/ _jsxs(Form, {
                                form: form,
                                layout: "vertical",
                                onFinish: handleSubmit,
                                children: [
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        label: /*#__PURE__*/ _jsx(FormattedMessage, {
                                            id: "intro_modal.form.role.label"
                                        }),
                                        name: "role",
                                        rules: [
                                            {
                                                required: true,
                                                message: roleValidationText
                                            }
                                        ],
                                        children: /*#__PURE__*/ _jsx(Select, {
                                            closeMenuOnSelect: true,
                                            isClearable: true,
                                            isLoading: isRolesLoading,
                                            options: rolesOptions,
                                            placeholder: userType === UserType.Partner ? /*#__PURE__*/ _jsx(FormattedMessage, {
                                                id: "settings.team_members.invite.placeholder.role_choose_only"
                                            }) : /*#__PURE__*/ _jsx(FormattedMessage, {
                                                id: "settings.team_members.invite.placeholder.role"
                                            })
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        name: "team_size",
                                        rules: [
                                            {
                                                required: true,
                                                message: teamValidationText
                                            }
                                        ],
                                        label: /*#__PURE__*/ _jsx(FormattedMessage, {
                                            id: "intro_modal.form.team_size.label"
                                        }),
                                        children: /*#__PURE__*/ _jsx(Select, {
                                            options: TEAM_OPTIONS.map((option)=>({
                                                    label: option,
                                                    value: option
                                                }))
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        name: "industry",
                                        rules: [
                                            {
                                                required: true,
                                                message: industryValidationText
                                            }
                                        ],
                                        label: /*#__PURE__*/ _jsx(FormattedMessage, {
                                            id: "intro_modal.form.industry.label"
                                        }),
                                        children: /*#__PURE__*/ _jsx(Select, {
                                            options: INDUSTRY_OPTIONS.map((option)=>({
                                                    label: translate(option.key),
                                                    value: option.value
                                                }))
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Form.Item, {
                                        name: "goals",
                                        rules: [
                                            {
                                                required: true,
                                                message: goalsValidationText
                                            }
                                        ],
                                        label: /*#__PURE__*/ _jsx(FormattedMessage, {
                                            id: "intro_modal.form.goals.label"
                                        }),
                                        children: /*#__PURE__*/ _jsx(Checkbox.Group, {
                                            children: /*#__PURE__*/ _jsx(Space, {
                                                direction: "vertical",
                                                children: GOAL_OPTIONS.map((option)=>/*#__PURE__*/ _jsx(Checkbox, {
                                                        value: option.value,
                                                        children: translate(option.key)
                                                    }, option.key))
                                            })
                                        })
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Footer, {
                        children: /*#__PURE__*/ _jsx(Button, {
                            type: "primary",
                            iconPlacement: "right",
                            icon: /*#__PURE__*/ _jsx(ArrowRight16, {}),
                            onClick: ()=>form.submit(),
                            loading: isLoading,
                            "data-cy": "intro_modal.form.button",
                            children: /*#__PURE__*/ _jsx(FormattedMessage, {
                                id: "intro_modal.form.button"
                            })
                        })
                    })
                ]
            })
        ]
    });
};
export const IntroModal = ()=>{
    const { user: { firstName, role, userType, emailId }, userMeta = {} } = useUser();
    const { metaData } = userMeta;
    const [visible, { setFalse: closeModal }] = useBoolean(!(metaData === null || metaData === void 0 ? void 0 : metaData.profileSetupDone));
    const [rhState, setRhState] = useState(null);
    if (!visible) {
        return null;
    }
    return /*#__PURE__*/ _jsx(StyledModal, {
        width: rhState === 2 ? 672 : 812,
        open: visible,
        closable: false,
        footer: null,
        centered: true,
        maskStyle: modalMaskCSS,
        zIndex: 1012,
        destroyOnClose: true,
        children: /*#__PURE__*/ _jsx(IntroModalContent, {
            firstName: firstName,
            emailId: emailId,
            userType: userType,
            originalRole: role,
            onFinish: closeModal,
            rhState: rhState,
            setRhState: setRhState
        })
    });
};
